import { CheckboxGroup, Colors } from '@amelio/ui-library';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { canScheduleReminder, OBJECTIVE_REMINDERS } from '../../objectiveReminders';

import type { ObjectiveFormData } from '../../validation';
import type { ObjectiveReminderValue } from '@/constants/ObjectiveReminder';
import type { TranslationKey } from '@/i18n-key';
import type { Control } from 'react-hook-form';

import { ObjectiveReminder } from '@/constants/ObjectiveReminder';
import { parseIsoDate } from '@/utils/dateUtils';

const REMINDER_LABELS: Record<ObjectiveReminderValue, TranslationKey> = {
    [ObjectiveReminder.TWO_MONTHS_BEFORE_END]: 'objectives.reminders.twoMonthsBeforeEnd',
    [ObjectiveReminder.ONE_MONTH_BEFORE_END]: 'objectives.reminders.oneMonthBeforeEnd',
    [ObjectiveReminder.TWO_WEEKS_BEFORE_END]: 'objectives.reminders.twoWeeksBeforeEnd',
};

interface ObjectiveReminderFieldsProps {
    control: Control<ObjectiveFormData>;
}

/**
 * The reminders the holders receive before the objective's end date while it is not complete. A
 * reminder whose day has passed, or any reminder while there is no end date, cannot be ticked; one
 * already ticked can always be unticked.
 */
export default function ObjectiveReminderFields({ control }: ObjectiveReminderFieldsProps) {
    const { t } = useTranslation();
    const endDate = useWatch({ control, name: 'endDate' });
    const today = new Date();
    const schedulable = new Set(
        OBJECTIVE_REMINDERS.filter((reminder) => canScheduleReminder(reminder, endDate, today)),
    );
    const hasEndDate = !!parseIsoDate(endDate);

    return (
        <Controller
            name={'reminders'}
            control={control}
            render={({ field }) => {
                const options = OBJECTIVE_REMINDERS.map((reminder) => ({
                    value: reminder,
                    label: t(REMINDER_LABELS[reminder]),
                    disabled: !schedulable.has(reminder) && !field.value.includes(reminder),
                }));

                return (
                    <div className={'flex flex-col gap-2'}>
                        <CheckboxGroup
                            name={'reminders'}
                            label={t('objectives.reminders.label')}
                            legendColor={Colors.NAVY}
                            options={options}
                            values={field.value}
                            orientation={'horizontal'}
                            onChange={(values) =>
                                field.onChange(
                                    OBJECTIVE_REMINDERS.filter((reminder) =>
                                        values.includes(reminder),
                                    ),
                                )
                            }
                            dataTestId={'objective-form-reminders'}
                        />

                        {!hasEndDate && (
                            <p className={'text-obj-muted text-sm'}>
                                {t('objectives.reminders.needsEndDate')}
                            </p>
                        )}

                        {hasEndDate && schedulable.size < OBJECTIVE_REMINDERS.length && (
                            <p className={'text-obj-muted text-sm'}>
                                {t('objectives.reminders.datePassed')}
                            </p>
                        )}
                    </div>
                );
            }}
        />
    );
}
